import { useMutation, useQueryClient } from '@tanstack/react-query';
import produce from 'immer';
import { omit } from 'lodash';

import useToastMsg from '../../common/useToastMsg';
import ProductApi from '../../../lib/api/product/ProductApi';
import { setRequestForm } from '../../../lib/common';
import {
  FULFILLMENT_PRODUCT_MANAGEMENT_TYPE_LIST,
  PRODUCT_MANAGEMENT_TYPE,
  PRODUCT_MANAGEMENT_TYPE_TEXT,
} from '../../../constants/service/app/productConstants';
import { PRODUCT } from '../../../constants/queryKeys';
import productToastMsg from '../../../constants/service/toastMsg/productToastMsg';
import { COMMON_RES_CODE } from '../../../constants/responseCode/commonResCode';
import { extractErrorMessage } from '../../../lib/utils/checkErrorResponse';

const useSelectBrandMutate = (form) => {
  const queryClient = useQueryClient();
  const toastMsg = useToastMsg();

  return useMutation({
    mutationFn: async (storeId) => {
      const result = await ProductApi.getStoreAsAndDeliveryInfo(setRequestForm({ storeId }, true));

      if (result?.status !== COMMON_RES_CODE.SUCCESS) {
        throw new Error(result.msg);
      }

      queryClient.setQueryData([...PRODUCT.PRODUCT_DETAIL], (prev) => {
        return produce(prev, (draft) => {
          const { productDetail } = result.data;

          /**
           *  스토어 ID 변경
           */
          draft.productDetail.proStatusAndBrandInfo.storeId = storeId;

          /**
           *  상점 상태 변경
           */
          draft.productDetail.proStatusAndBrandInfo.storeStatus =
            result.data.productDetail.proStatusAndBrandInfo.storeStatus;

          /**
           *  재고/옵션
           */
          const { brandFeeInfoList = [] } = productDetail.proStockAndOption;
          const isFulfillmentProduct = brandFeeInfoList.find((item) =>
            FULFILLMENT_PRODUCT_MANAGEMENT_TYPE_LIST.includes(item.productManagementType),
          );
          const targetFeeInfo = isFulfillmentProduct
            ? brandFeeInfoList.find(
                (item) =>
                  item.productManagementType === prev.productDetail.proStockAndOption.feeInfo.productManagementType,
              )
            : brandFeeInfoList[0];

          // form 에 optionList, feeInfo data 가 있을 경우 form 에 있는 data 사용
          const formOptionList = form.getFieldValue('optionList');
          const formFeeInfo = form.getFieldValue('feeInfo');
          const optionKeyName =
            form.getFieldValue('optionKeyName') ?? draft.productDetail.proStockAndOption.optionKeyName;
          const choiceOption = form.getFieldValue('choiceOption') ?? draft.productDetail.proStockAndOption.choiceOption;
          const optionList = formOptionList?.length ? formOptionList : draft.productDetail.proStockAndOption.optionList;
          const feeInfo = formFeeInfo || draft.productDetail.proStockAndOption.feeInfo;

          draft.productDetail.proStockAndOption = {
            ...draft.productDetail.proStockAndOption,
            ...productDetail.proStockAndOption,
            brandFeeInfoList,
            optionKeyName,
            choiceOption,
            optionList: optionList?.length
              ? optionList.map((item) => ({
                  ...item,
                  productManagementTypeSelectList: isFulfillmentProduct
                    ? item.productManagementTypeSelectList.map((subItem) => ({ ...subItem, disabled: false }))
                    : item.productManagementTypeSelectList.map((subItem) => ({
                        ...subItem,
                        disabled: subItem.name !== PRODUCT_MANAGEMENT_TYPE_TEXT[PRODUCT_MANAGEMENT_TYPE.ENTER],
                      })),
                  feeInfo: {
                    ...item.feeInfo,
                    ...omit(targetFeeInfo, ['productManagementType']),
                  },
                }))
              : [],
            feeInfo: {
              ...feeInfo,
              ...targetFeeInfo,
              productManagementTypeSelectList: isFulfillmentProduct
                ? feeInfo?.productManagementTypeSelectList.map((item) => ({
                    ...item,
                    disabled: false,
                  }))
                : feeInfo?.productManagementTypeSelectList.map((item) => ({
                    ...item,
                    disabled: item.name !== PRODUCT_MANAGEMENT_TYPE_TEXT[PRODUCT_MANAGEMENT_TYPE.ENTER],
                  })),
            },
          };

          /**
           *  배송 정보
           */
          draft.productDetail.proDeliveryInfo = {
            ...draft.productDetail.proDeliveryInfo,
            ...productDetail.proDeliveryInfo,
          };

          /**
           *  A/S 정보
           */
          draft.productDetail.proAfterService = productDetail.proAfterService;

          form.setFieldsValue({
            asInform: productDetail.proAfterService.asInform,
            caution: productDetail.proAfterService.caution,
            qualityAssurance: productDetail.proAfterService.qualityAssurance,
            origin: productDetail.proAfterService.origin,
            manufacturer: productDetail.proAfterService.manufacturer,
          });
        });
      });
    },
    onSuccess: () => {
      toastMsg.success(productToastMsg.GET_STORE_AS_AND_DELIVERY_INFO_REQUEST_SUCCESS);
    },
    onError: (e) => {
      toastMsg.error(extractErrorMessage(e, productToastMsg.GET_STORE_AS_AND_DELIVERY_INFO_REQUEST_ERROR));
    },
  });
};

export default useSelectBrandMutate;
